import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Button,
  Stack,
  Avatar,
  LinearProgress,
  useMediaQuery,
  useTheme
} from '@mui/material';
import TableSortLabel from '@mui/material/TableSortLabel';
import Pagination from '@mui/material/Pagination';
import {
  AttachMoney as MoneyIcon,
  ShoppingCart as CartIcon,
  AccountBalance as InvestmentIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  ShowChart as ChartIcon,
  LocalAtm as CashIcon,
  CompareArrows as CompareIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import StatsService from '../../services/stats';
import { ccyFormat } from '../../helpers/helper';
import { CircularProgressCustom } from '../../components';

const StatCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
  <Card
    elevation={0}
    sx={{
      height: '100%',
      background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
      border: `1px solid ${color}30`,
      borderRadius: 3,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 8px 24px ${color}40`,
      }
    }}
  >
    <CardContent>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
        <Box flex={1}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={700} color={color} mt={1} mb={0.5}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
          {trend && (
            <Chip
              label={trend}
              size="small"
              sx={{
                mt: 1,
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 600,
                background: `${color}20`,
                color: color
              }}
            />
          )}
        </Box>
        <Avatar
          sx={{
            bgcolor: `${color}20`,
            color: color,
            width: 56,
            height: 56
          }}
        >
          <Icon fontSize="large" />
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const ExpensesStats = ({ storeId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const now = new Date();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState({ items: [], total: 0, page: 1, page_size: 20, total_pages: 1 });
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [dateRange, setDateRange] = useState({
    date_from: now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-01',
    date_to: now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0'),
  });

  const loadStats = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      const data = await StatsService.getExpensesStats(storeId, { ...params, include_revenue: true });
      setStats(data);
    } catch (error) {
      console.error('Error loading expenses stats:', error);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  const loadExpenses = useCallback(async (params = {}) => {
    try {
      setExpensesLoading(true);
      const data = await StatsService.getExpenses(storeId, params);
      setExpenses(data);
    } catch (error) {
      console.error('Error loading expenses list:', error);
    } finally {
      setExpensesLoading(false);
    }
  }, [storeId]);

  const debounceRef = useRef(null);

  // Load both on date range change or initial load
  useEffect(() => {
    if (storeId) {
      loadStats({ ...dateRange });
      setPage(1);
      loadExpenses({ ...dateRange, q: searchQuery, page: 1, page_size: pageSize, sort_by: sortBy, sort_order: sortOrder });
    }
  }, [storeId, loadStats, loadExpenses, dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only refresh expenses list when search changes (debounced)
  useEffect(() => {
    if (!storeId) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      loadExpenses({ ...dateRange, q: searchQuery, page: 1, page_size: pageSize, sort_by: sortBy, sort_order: sortOrder });
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh expenses when sort or page changes
  useEffect(() => {
    if (!storeId) return;
    loadExpenses({ ...dateRange, q: searchQuery, page, page_size: pageSize, sort_by: sortBy, sort_order: sortOrder });
  }, [page, sortBy, sortOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterByDate = () => {
    loadStats({ ...dateRange });
    setPage(1);
    loadExpenses({ ...dateRange, q: searchQuery, page: 1, page_size: pageSize, sort_by: sortBy, sort_order: sortOrder });
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setPage(1);
    loadExpenses({ ...dateRange, q: '', page: 1, page_size: pageSize, sort_by: sortBy, sort_order: sortOrder });
  };

  const handleResetFilter = () => {
    const now = new Date();
    const newDateRange = {
      date_from: now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-01',
      date_to: now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0'),
    };
    setDateRange(newDateRange);
    setSearchQuery('');
    setPage(1);
    loadStats({ ...newDateRange });
    loadExpenses({ ...newDateRange, q: '', page: 1, page_size: pageSize, sort_by: sortBy, sort_order: sortOrder });
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const getExpenseTypeConfig = (type) => {
    switch ((type || '').toLowerCase()) {
      case 'gasto':      return { color: '#ef5350', chipColor: 'error',   isIncome: false };
      case 'inversion':  return { color: '#FF6B35', chipColor: 'warning', isIncome: false };
      case 'reinversion':return { color: '#66BB6A', chipColor: 'success', isIncome: true  };
      default:           return { color: '#9e9e9e', chipColor: 'default', isIncome: false };
    }
  };

  const formatDateLabel = (dateValue) => {
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) return dateValue;
    return parsed.toLocaleDateString('es-ES', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgressCustom size={200} />
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h6" color="text.secondary" textAlign="center">
          No hay datos disponibles
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          📊 Estadísticas de Gastos
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {stats.period || 'Último mes'}
        </Typography>
      </Box>

      {/* Date Filter */}
      <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3, bgcolor: 'background.paper' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            label="Fecha desde"
            type="date"
            value={dateRange.date_from}
            onChange={(e) => setDateRange({ ...dateRange, date_from: e.target.value })}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
          />
          <TextField
            label="Fecha hasta"
            type="date"
            value={dateRange.date_to}
            onChange={(e) => setDateRange({ ...dateRange, date_to: e.target.value })}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
          />
          <TextField
            label="Buscar descripción"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            fullWidth
            placeholder="ej: alquiler, serv..."
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />,
              endAdornment: searchQuery ? (
                <ClearIcon
                  fontSize="small"
                  sx={{ cursor: 'pointer', color: 'text.secondary' }}
                  onClick={handleClearSearch}
                />
              ) : null,
            }}
          />
          <Button
            variant="contained"
            onClick={handleFilterByDate}
            size="large"
            sx={{ minWidth: 120 }}
          >
            Filtrar
          </Button>
          <Button
            variant="outlined"
            onClick={handleResetFilter}
            size="large"
            sx={{ minWidth: 120 }}
          >
            Limpiar
          </Button>
        </Stack>
      </Paper>

      {/* Main Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Gastos"
            value={ccyFormat(stats.total_expenses || 0)}
            subtitle="Todos los gastos"
            icon={MoneyIcon}
            color="#ef5350"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Compras"
            value={ccyFormat(stats.total_purchases || 0)}
            subtitle={`${stats.total_items_purchased || 0} artículos`}
            icon={CartIcon}
            color="#FF6B35"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Otros Gastos"
            value={ccyFormat(stats.total_other_expenses || 0)}
            subtitle="Gastos operativos"
            icon={ReceiptIcon}
            color="#FFA726"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Inversión"
            value={ccyFormat(stats.total_investment || 0)}
            subtitle={`Reinv (ingreso): ${ccyFormat(stats.total_reinvestment_income || 0)}`}
            icon={InvestmentIcon}
            color="#AB47BC"
          />
        </Grid>
      </Grid>

      {/* Profitability Metrics */}
      {stats.total_revenue > 0 && (
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Ingresos Totales"
              value={ccyFormat(stats.total_revenue || 0)}
              subtitle="Ventas del período"
              icon={TrendingUpIcon}
              color="#66BB6A"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Ganancia Neta"
              value={ccyFormat(stats.net_profit || 0)}
              subtitle={`${(stats.profit_margin || 0).toFixed(2)}% margen`}
              icon={ChartIcon}
              color="#26A69A"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="ROI"
              value={`${(stats.roi || 0).toFixed(2)}%`}
              subtitle="Retorno de inversión"
              icon={CompareIcon}
              color="#42A5F5"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Precio Promedio"
              value={ccyFormat(stats.average_purchase_price || 0)}
              subtitle="Por artículo"
              icon={CashIcon}
              color="#5C6BC0"
            />
          </Grid>
        </Grid>
      )}

      {/* Expenses by Type */}
      {stats.expenses_by_type && Object.keys(stats.expenses_by_type).length > 0 && (
        <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={700} mb={3}>
            💰 Gastos por Tipo
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(stats.expenses_by_type).map(([type, amount]) => {
              const typeConfig = getExpenseTypeConfig(type);
              const allTypeTotal = Object.values(stats.expenses_by_type).reduce((s, v) => s + v, 0);
              return (
                <Grid item xs={12} sm={6} md={4} key={type}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'background.default',
                      border: '1px solid',
                      borderColor: typeConfig.isIncome ? `${typeConfig.color}50` : 'divider',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.8} mb={0.5}>
                      <Typography variant="caption" fontWeight={700} textTransform="uppercase" sx={{ color: typeConfig.color }}>
                        {type}
                      </Typography>
                      {typeConfig.isIncome && (
                        <Chip label="ingreso" size="small" color="success" sx={{ height: 16, fontSize: '0.65rem' }} />
                      )}
                    </Stack>
                    <Typography variant="h5" fontWeight={700} sx={{ color: typeConfig.color }}>
                      {ccyFormat(amount)}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={allTypeTotal > 0 ? (amount / allTypeTotal) * 100 : 0}
                      sx={{ mt: 1, height: 6, borderRadius: 3, bgcolor: `${typeConfig.color}20`, '& .MuiLinearProgress-bar': { bgcolor: typeConfig.color } }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {allTypeTotal > 0 ? `${((amount / allTypeTotal) * 100).toFixed(1)}% del período` : ''}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}

      {/* Expenses detail */}
      <Paper elevation={0} sx={{ p: { xs: 1.5, md: 3 }, mb: 4, borderRadius: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight={700}>
            🧾 Detalle de Gastos
          </Typography>
          <Chip label={`${expenses.total} registros`} size="small" variant="outlined" />
        </Stack>

        {expensesLoading && <LinearProgress sx={{ mb: 1, borderRadius: 2 }} />}

        {isMobile ? (
          <Stack spacing={1.2}>
            {expenses.items.map((expense, i) => {
              const typeConfig = getExpenseTypeConfig(expense.type);
              return (
                <Paper
                  key={expense._id || i}
                  elevation={0}
                  sx={{ border: '1px solid', borderColor: typeConfig.isIncome ? `${typeConfig.color}40` : 'divider', borderRadius: 2, p: 1.2 }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box flex={1} mr={1}>
                      <Typography variant="body2" fontWeight={600}>{expense.description || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {expense.created_at ? formatDateLabel(expense.created_at) : '—'}
                        {expense.user ? ` · ${expense.user}` : ''}
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={700} sx={{ color: typeConfig.color }}>
                      {typeConfig.isIncome ? '+' : '-'}${ccyFormat(expense.amount || 0)}
                    </Typography>
                  </Stack>
                  {expense.type && (
                    <Chip label={expense.type} size="small" color={typeConfig.chipColor} variant="outlined" sx={{ mt: 0.8, fontSize: '0.7rem', height: 20 }} />
                  )}
                </Paper>
              );
            })}
          </Stack>
        ) : (
          <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'created_at'}
                      direction={sortBy === 'created_at' ? sortOrder : 'desc'}
                      onClick={() => handleSort('created_at')}
                    >
                      <strong>Fecha</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'type'}
                      direction={sortBy === 'type' ? sortOrder : 'desc'}
                      onClick={() => handleSort('type')}
                    >
                      <strong>Tipo</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'description'}
                      direction={sortBy === 'description' ? sortOrder : 'desc'}
                      onClick={() => handleSort('description')}
                    >
                      <strong>Descripción</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortBy === 'amount'}
                      direction={sortBy === 'amount' ? sortOrder : 'desc'}
                      onClick={() => handleSort('amount')}
                    >
                      <strong>Monto</strong>
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expenses.items.map((expense, i) => {
                  const typeConfig = getExpenseTypeConfig(expense.type);
                  return (
                    <TableRow
                      key={expense._id || i}
                      sx={{ '&:hover': { bgcolor: 'action.hover' }, ...(typeConfig.isIncome && { bgcolor: '#66BB6A08' }) }}
                    >
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {expense.created_at ? formatDateLabel(expense.created_at) : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {expense.type && (
                          <Chip label={expense.type} size="small" color={typeConfig.chipColor} variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{expense.description || '—'}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700} sx={{ color: typeConfig.color }}>
                          {typeConfig.isIncome ? '+' : '-'}${ccyFormat(expense.amount || 0)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {expenses.total_pages > 1 && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={expenses.total_pages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              size={isMobile ? 'small' : 'medium'}
            />
          </Box>
        )}
      </Paper>

      {/* Top Expensive Articles */}
      {stats.top_expensive_articles && stats.top_expensive_articles.length > 0 && (
        <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={700} mb={3}>
            📦 Artículos Más Costosos
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>#</strong></TableCell>
                  <TableCell><strong>Artículo</strong></TableCell>
                  <TableCell><strong>SKU</strong></TableCell>
                  <TableCell align="center"><strong>Cantidad</strong></TableCell>
                  <TableCell align="right"><strong>Costo Promedio</strong></TableCell>
                  <TableCell align="right"><strong>Costo Total</strong></TableCell>
                  <TableCell><strong>Fecha Compra</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.top_expensive_articles.map((article, index) => (
                  <TableRow
                    key={article.article_id}
                    sx={{
                      '&:hover': { bgcolor: 'action.hover' },
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <TableCell>
                      <Chip
                        label={index + 1}
                        size="small"
                        color={index === 0 ? 'error' : index === 1 ? 'warning' : 'default'}
                        sx={{ fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {article.article_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {article.sku}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={article.quantity}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="primary" fontWeight={600}>
                        {ccyFormat(article.average_cost)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight={700} color="error.main">
                        {ccyFormat(article.total_cost)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {article.purchase_date}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Daily Expenses */}
      {stats.daily_expenses && stats.daily_expenses.length > 0 && (
        <Paper elevation={0} sx={{ p: { xs: 1.5, md: 3 }, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={700} mb={3}>
            📅 Gastos Diarios
          </Typography>

          {isMobile ? (
            <Stack spacing={1.2}>
              {stats.daily_expenses.map((day) => (
                <Paper
                  key={day.date}
                  elevation={0}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 1.2,
                    background: 'linear-gradient(135deg, #ef535012 0%, #ef535005 100%)'
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.8}>
                    <Typography variant="subtitle2" fontWeight={700}>{formatDateLabel(day.date)}</Typography>
                    <Chip
                      size="small"
                      color="error"
                      variant="filled"
                      label={`$ ${ccyFormat(day.total_expenses || 0)}`}
                      sx={{ fontWeight: 700 }}
                    />
                  </Stack>

                  <Grid container spacing={0.8}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Compras</Typography>
                      <Typography variant="body2" fontWeight={600}>{`$ ${ccyFormat(day.purchases || 0)}`}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Otros gastos</Typography>
                      <Typography variant="body2" fontWeight={600}>{`$ ${ccyFormat(day.other_expenses || 0)}`}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Inversión</Typography>
                      <Typography variant="body2" fontWeight={600}>{`$ ${ccyFormat(day.investment || 0)}`}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Reinversión</Typography>
                      <Typography variant="body2" fontWeight={600}>{`$ ${ccyFormat(day.reinvestment || 0)}`}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Stack>
          ) : (
            <TableContainer sx={{ maxHeight: 500, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Fecha</strong></TableCell>
                    <TableCell align="right"><strong>Total</strong></TableCell>
                    <TableCell align="right"><strong>Compras</strong></TableCell>
                    <TableCell align="right"><strong>Otros Gastos</strong></TableCell>
                    <TableCell align="right"><strong>Inversión</strong></TableCell>
                    <TableCell align="right"><strong>Reinversión</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.daily_expenses.map((day) => (
                    <TableRow
                      key={day.date}
                      sx={{
                        '&:hover': { bgcolor: 'action.hover' },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDateLabel(day.date)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontWeight={700} color="error.main">
                          {`$ ${ccyFormat(day.total_expenses || 0)}`}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {`$ ${ccyFormat(day.purchases || 0)}`}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {`$ ${ccyFormat(day.other_expenses || 0)}`}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {`$ ${ccyFormat(day.investment || 0)}`}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {`$ ${ccyFormat(day.reinvestment || 0)}`}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default ExpensesStats;
